import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/movieStore';

const Layout = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* 导航栏 */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'glass shadow-lg shadow-black/20'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center transform group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                影视聚合
              </span>
            </Link>

            {/* 桌面端导航链接 */}
            <div className="hidden md:flex items-center space-x-1">
              <NavLink to="/" icon="🏠" label="首页" />
              <NavLink to="/search" icon="🔍" label="搜索" />
              {isAuthenticated && (
                <>
                  <NavLink to="/favorites" icon="❤️" label="收藏" />
                  <NavLink to="/history" icon="📜" label="历史" />
                </>
              )}
              {user?.role === 'admin' && (
                <NavLink to="/admin" icon="⚙️" label="管理" />
              )}
            </div>

            {/* 用户操作区 */}
            <div className="hidden md:flex items-center space-x-3">
              {isAuthenticated ? (
                <div className="flex items-center space-x-3">
                  <Link
                    to="/user"
                    className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-slate-700/50 hover:bg-slate-600/50 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center text-white text-sm font-medium">
                      {user?.username?.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm text-gray-300">{user?.username}</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    退出
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Link
                    to="/login"
                    className="px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors"
                  >
                    登录
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-2 text-sm bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all transform hover:scale-105"
                  >
                    注册
                  </Link>
                </div>
              )}
            </div>

            {/* 移动端菜单按钮 */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-slate-700/50 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* 移动端菜单 */}
        {isMobileMenuOpen && (
          <div className="md:hidden glass border-t border-slate-700/50">
            <div className="px-4 py-3 space-y-2">
              <MobileNavLink to="/" icon="🏠" label="首页" onClick={() => setIsMobileMenuOpen(false)} />
              <MobileNavLink to="/search" icon="🔍" label="搜索" onClick={() => setIsMobileMenuOpen(false)} />
              {isAuthenticated && (
                <>
                  <MobileNavLink to="/favorites" icon="❤️" label="收藏" onClick={() => setIsMobileMenuOpen(false)} />
                  <MobileNavLink to="/history" icon="📜" label="历史" onClick={() => setIsMobileMenuOpen(false)} />
                  <MobileNavLink to="/user" icon="👤" label="个人中心" onClick={() => setIsMobileMenuOpen(false)} />
                </>
              )}
              {user?.role === 'admin' && (
                <MobileNavLink to="/admin" icon="⚙️" label="管理" onClick={() => setIsMobileMenuOpen(false)} />
              )}
              <div className="pt-2 border-t border-slate-700/50">
                {isAuthenticated ? (
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-red-400 hover:bg-slate-700/50 rounded-lg transition-colors"
                  >
                    退出登录
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <Link
                      to="/login"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex-1 px-4 py-2 text-center text-gray-300 hover:bg-slate-700/50 rounded-lg transition-colors"
                    >
                      登录
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex-1 px-4 py-2 text-center bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg"
                    >
                      注册
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* 主内容区 */}
      <main className="pt-16">
        <Outlet />
      </main>

      {/* 页脚 */}
      <footer className="mt-20 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* 品牌信息 */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                  影视聚合
                </span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                聚合多个影视资源平台，为您提供一站式的影视搜索、观看和管理体验。
                支持多源搜索、高清播放、个人收藏等功能。
              </p>
            </div>

            {/* 快速链接 */}
            <div>
              <h3 className="text-white font-semibold mb-4">快速链接</h3>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white text-sm transition-colors">首页</Link></li>
                <li><Link to="/search" className="text-gray-400 hover:text-white text-sm transition-colors">搜索</Link></li>
                <li><Link to="/favorites" className="text-gray-400 hover:text-white text-sm transition-colors">我的收藏</Link></li>
                <li><Link to="/history" className="text-gray-400 hover:text-white text-sm transition-colors">观看历史</Link></li>
              </ul>
            </div>

            {/* 帮助与支持 */}
            <div>
              <h3 className="text-white font-semibold mb-4">帮助与支持</h3>
              <ul className="space-y-2">
                <li><span className="text-gray-400 text-sm">使用帮助</span></li>
                <li><span className="text-gray-400 text-sm">反馈建议</span></li>
                <li><span className="text-gray-400 text-sm">关于我们</span></li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-slate-800">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-gray-500 text-sm">
                © 2024 影视聚合. All rights reserved.
              </p>
              <p className="text-gray-500 text-sm">
                Powered by Cloudflare
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// 桌面端导航链接组件
const NavLink = ({ to, icon, label }: { to: string; icon: string; label: string }) => (
  <Link
    to={to}
    className="flex items-center space-x-1 px-3 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-slate-700/50 transition-all"
  >
    <span>{icon}</span>
    <span className="text-sm">{label}</span>
  </Link>
);

// 移动端导航链接组件
const MobileNavLink = ({
  to,
  icon,
  label,
  onClick,
}: {
  to: string;
  icon: string;
  label: string;
  onClick: () => void;
}) => (
  <Link
    to={to}
    onClick={onClick}
    className="flex items-center space-x-3 px-4 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-slate-700/50 transition-all"
  >
    <span>{icon}</span>
    <span>{label}</span>
  </Link>
);

export default Layout;
